import { Box, Divider, Grid, Paper, Typography } from '@mui/material'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import { useTranslations } from 'next-intl'

interface CostOfLivingProps {
    priceRows: { label: string; value: string }[]
    infoFacts: { label: string; value: string }[]
    tipping: string
    tippingLabel?: string
    currencySymbol: string
    sectionTitle?: string
    priceEstimatesTitle?: string
}

const CostOfLiving: React.FC<CostOfLivingProps> = ({ priceRows, infoFacts, tipping, tippingLabel, currencySymbol, sectionTitle, priceEstimatesTitle }) => {
    const t = useTranslations('destinationPage')
    return (
        <Box component="section" sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                <AttachMoneyOutlinedIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography
                    component="h2"
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.1rem' } }}
                >
                    {sectionTitle ?? t('costOfLiving')}
                </Typography>
            </Box>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {infoFacts.length > 0 && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2 }, height: '100%' }}>
                            <Box component="dl" sx={{ m: 0 }}>
                                {infoFacts.map((fact, i) => (
                                    <Box key={fact.label}>
                                        <Box sx={{ py: 0.75 }}>
                                            <Typography component="dt" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {fact.label}
                                            </Typography>
                                            <Typography component="dd" variant="body2" sx={{ fontWeight: 600, m: 0 }}>
                                                {fact.value}
                                            </Typography>
                                        </Box>
                                        {i < infoFacts.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                )}

                {priceRows.length > 0 && (
                    <Grid size={{ xs: 12, sm: infoFacts.length > 0 ? 8 : 12 }}>
                        <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                {priceEstimatesTitle ?? t('approximatePrices', { symbol: currencySymbol })}
                            </Typography>
                            <Box component="dl" sx={{ m: 0 }}>
                                {priceRows.map((row, i) => (
                                    <Box key={row.label}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
                                            <Typography component="dt" variant="body2" color="text.secondary">{row.label}</Typography>
                                            <Typography component="dd" variant="body2" sx={{ fontWeight: 600, m: 0 }}>{row.value}</Typography>
                                        </Box>
                                        {i < priceRows.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {tipping && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.6 }}>
                    {tippingLabel && <Box component="span" sx={{ fontWeight: 600 }}>{tippingLabel}: </Box>}
                    {tipping}
                </Typography>
            )}
        </Box>
    )
}

export default CostOfLiving
export type { CostOfLivingProps }
